/**
 * Global single-audio-source manager for videos.
 *
 * Only ONE video may produce audio at a time across the entire app (feed,
 * Forums, Tribes, Yap Details, fullscreen). Each audible <video> registers a
 * unique id; whenever the active id changes, every other instance force-mutes
 * itself (and updates its speaker icon to match).
 *
 * Intentionally a tiny module-level pub/sub (no provider needed) so it works
 * for the many YapVideoPlayer instances scattered across the tree.
 */
type Listener = (activeId: string | null) => void;

let _active: string | null = null;
const _listeners = new Set<Listener>();

export const videoAudio = {
  getActive(): string | null {
    return _active;
  },
  /** Make `id` the sole audio source (pass null for "no audio anywhere"). */
  setActive(id: string | null): void {
    if (_active === id) return;
    _active = id;
    _listeners.forEach((l) => { try { l(_active); } catch { /* ignore */ } });
  },
  /** Clear the active source only if it is currently `id` (e.g. on mute / scroll-off / unmount). */
  clearIfActive(id: string): void {
    if (_active === id) this.setActive(null);
  },
  subscribe(l: Listener): () => void {
    _listeners.add(l);
    return () => { _listeners.delete(l); };
  },
};
